import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { usePageTitle } from '../hooks/usePageTitle';

const STATUS_OPTIONS = [
  { value: 'inquiry', label: 'Inquiry', color: 'border-red-500/30 bg-red-500/10 text-red-400' },
  { value: 'tentative', label: 'Tentative', color: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
  { value: 'confirmed', label: 'Confirmed', color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' },
  { value: 'completed', label: 'Completed', color: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400' },
];

const getStatusBadge = (status) => {
  const statusOption = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return statusOption;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'M/d/yy');
  } catch {
    return dateStr;
  }
};

const Events = () => {
  usePageTitle('Events');
  const { isViewer } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pmFilter, setPmFilter] = useState('');
  const [aeFilter, setAeFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ project_managers: [], account_execs: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [sortBy, setSortBy] = useState('start_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [initialPageLoaded, setInitialPageLoaded] = useState(false);
  const navigate = useNavigate();

  // Load filter options on mount
  useEffect(() => {
    eventsAPI.getFilterOptions().then(res => setFilterOptions(res.data)).catch(() => {});
  }, []);

  // On mount, calculate starting page to show events from ~3 days ago
  useEffect(() => {
    const calcStartPage = async () => {
      try {
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        const dateStr = threeDaysAgo.toISOString().split('T')[0];
        const response = await eventsAPI.getPageForDate(dateStr, limit);
        if (response.data && response.data.page) {
          setPage(response.data.page);
        }
      } catch {
        // fallback to page 1
      } finally {
        setInitialPageLoaded(true);
      }
    };
    calcStartPage();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pmFilter, aeFilter]);

  useEffect(() => {
    if (initialPageLoaded) {
      loadEvents();
    }
  }, [search, statusFilter, pmFilter, aeFilter, page, limit, sortBy, sortOrder, initialPageLoaded]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (pmFilter && pmFilter !== 'all') params.project_manager_id = pmFilter;
      if (aeFilter && aeFilter !== 'all') params.account_exec_id = aeFilter;
      
      const response = await eventsAPI.list(params);
      
      if (response.data.data && response.data.pagination) {
        setEvents(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setEvents(response.data);
        setPagination({
          total: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'start_date' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> events
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(1)} disabled={!has_prev}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page - 1)} disabled={!has_prev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              ))}
            </div>

            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page + 1)} disabled={!has_next}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(total_pages)} disabled={!has_next}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Calendar className="h-8 w-8 text-emerald-400" />
            Events
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your event productions and projects
          </p>
        </div>
        {!isViewer && (
          <Button asChild data-testid="add-event-btn">
            <Link to="/events/new">
              <Plus className="h-4 w-4 mr-2" />
              New Event
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search events by name or flex #..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="search-events"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pmFilter} onValueChange={setPmFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50" data-testid="pm-filter">
                <SelectValue placeholder="Project Manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All PMs</SelectItem>
                {filterOptions.project_managers.map((pm) => (
                  <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={aeFilter} onValueChange={setAeFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50" data-testid="ae-filter">
                <SelectValue placeholder="Account Exec" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All AEs</SelectItem>
                {filterOptions.account_execs.map((ae) => (
                  <SelectItem key={ae.id} value={ae.id}>{ae.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : events.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('flex_number')}
                    >
                      <div className="flex items-center">
                        Flex #
                        {getSortIcon('flex_number')}
                      </div>
                    </TableHead>
                    <TableHead className="data-table">Status</TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('start_date')}
                    >
                      <div className="flex items-center">
                        Dates
                        {getSortIcon('start_date')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('event_name')}
                    >
                      <div className="flex items-center">
                        Event Name
                        {getSortIcon('event_name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('client_name')}
                    >
                      <div className="flex items-center">
                        Client
                        {getSortIcon('client_name')}
                      </div>
                    </TableHead>
                    <TableHead className="data-table">Location</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => {
                    const statusBadge = getStatusBadge(event.status);
                    return (
                      <TableRow
                        key={event.id}
                        className="border-border/50 hover:bg-muted/30 cursor-pointer"
                        onClick={() => navigate(`/events/${event.id}`)}
                        data-testid={`event-row-${event.id}`}
                      >
                        <TableCell className="text-muted-foreground mono text-sm">
                          {event.flex_number || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${statusBadge.color}`}>
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(event.start_date)}
                          {event.end_date && event.end_date !== event.start_date && (
                            <> - {formatDate(event.end_date)}</>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{event.event_name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {event.client_name || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {event.location_name || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No events found</p>
              {!isViewer && (
                <>
                  <p className="text-sm mt-1">Create your first event to get started</p>
                  <Button asChild className="mt-4" variant="outline">
                    <Link to="/events/new">
                      <Plus className="h-4 w-4 mr-2" />
                      New Event
                    </Link>
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Events;
